/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";

import { track } from "@/lib/tracking";
import { getConsent, subscribeConsent } from "@/lib/consent";

const STORAGE_KEY = "equisight_blog_popup_dismissed_until";
const VARIANT_KEY = "equisight_blog_popup_variant";
const DISMISS_DAYS = 7;
const SCROLL_THRESHOLD = 0.6; // 60% of document height
const EXPERIMENT = "blog_popup";

/**
 * Randomly assign variant A or B per browser, persisted in localStorage so
 * the same visitor consistently sees the same copy across page-views.
 * URL override: `?popup=A` / `?popup=B` for QA.
 */
const pickVariant = () => {
  if (typeof window === "undefined") return "A";
  // QA override via query string
  try {
    const force = new URLSearchParams(window.location.search).get("popup");
    if (force === "A" || force === "B") return force;
  } catch {
    /* ignore */
  }
  try {
    const existing = window.localStorage.getItem(VARIANT_KEY);
    if (existing === "A" || existing === "B") return existing;
    const next = Math.random() < 0.5 ? "A" : "B";
    window.localStorage.setItem(VARIANT_KEY, next);
    return next;
  } catch {
    return Math.random() < 0.5 ? "A" : "B";
  }
};

/**
 * Slide-up engagement popup shown to readers deep in blog/kennisbank content.
 * - Triggers when user has scrolled past 60% of the page
 * - Bottom-right on desktop, bottom-full-width on mobile
 * - Dismissed for 7 days via localStorage
 * - A/B tested via `props.variant` on every event
 */
export default function TryAppPopup({ ctaHref = "/#waitlist", source = "blog" }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const [consentDecided, setConsentDecided] = useState(
    () => getConsent().decided,
  );
  const variant = useMemo(() => pickVariant(), []);
  const eventProps = useMemo(
    () => ({ experiment: EXPERIMENT, variant, source }),
    [variant, source],
  );

  useEffect(() => {
    // Keep popup hidden while cookie banner is still demanding attention.
    if (consentDecided) return undefined;
    const unsubscribe = subscribeConsent((next) => {
      if (next?.decided) setConsentDecided(true);
    });
    return unsubscribe;
  }, [consentDecided]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!consentDecided) return;
    try {
      const until = Number(window.localStorage.getItem(STORAGE_KEY) || 0);
      if (until && Date.now() < until) {
        setClosed(true);
        return;
      }
    } catch {
      /* ignore storage errors */
    }

    let triggered = false;
    const onScroll = () => {
      if (triggered) return;
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = doc.scrollHeight;
      if (total <= 0) return;
      if (scrolled / total >= SCROLL_THRESHOLD) {
        triggered = true;
        setVisible(true);
        track("popup_view", { label: `popup-${variant}`, props: eventProps });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Initial check in case content is short and already past threshold
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [source, consentDecided, variant, eventProps]);

  const persistDismiss = () => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
      );
    } catch {
      /* ignore */
    }
  };

  const dismiss = () => {
    setVisible(false);
    setClosed(true);
    persistDismiss();
    track("popup_dismiss", { label: `popup-${variant}`, props: eventProps });
  };

  const handleCta = () => {
    track("cta_click", {
      label: "blog-popup",
      props: eventProps,
    });
    // Keep popup visible until navigation finishes; localStorage updates so it
    // won't reappear during the same session.
    persistDismiss();
  };

  if (closed || !visible) return null;

  // Variant copy keys
  const titleKey = variant === "B" ? "tryApp.titleB" : "tryApp.title";
  const bodyKey = variant === "B" ? "tryApp.bodyB" : "tryApp.body";
  const ctaKey = variant === "B" ? "tryApp.ctaB" : "common.betaTester";

  return (
    <div
      data-testid="blog-try-app-popup"
      data-variant={variant}
      role="dialog"
      aria-labelledby="try-app-popup-title"
      className="fixed inset-x-3 bottom-3 z-40 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[360px]"
    >
      <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] shadow-[0_20px_50px_-15px_rgba(31,42,32,0.45)] animate-[fadeInUp_400ms_ease-out_both]">
        {/* Decorative gradient stripe */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[hsl(var(--forest))] via-[hsl(var(--sand))] to-[hsl(var(--forest))]"
        />
        <button
          type="button"
          onClick={dismiss}
          data-testid="blog-try-app-popup-close"
          aria-label={t("tryApp.ariaClose")}
          className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-[hsl(var(--ink-soft))] transition-colors hover:bg-[hsl(var(--surface))] hover:text-[hsl(var(--ink))]"
        >
          <X className="h-4 w-4" strokeWidth={1.8} />
        </button>

        <div className="p-5 pr-9">
          <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            <Sparkles className="h-3 w-3" strokeWidth={1.8} />
            {t("tryApp.eyebrow")}
          </p>
          <h3
            id="try-app-popup-title"
            className="mt-2 font-display text-xl leading-tight text-[hsl(var(--ink))]"
          >
            {t(titleKey)}
          </h3>
          <p className="mt-2 text-[13px] leading-relaxed text-[hsl(var(--ink-soft))]">
            {t(bodyKey)}
          </p>
          <a
            href={ctaHref}
            onClick={handleCta}
            data-testid="blog-try-app-popup-cta"
            className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[hsl(var(--forest))] px-4 py-2 text-sm font-medium text-[hsl(var(--background))] transition-colors hover:bg-[hsl(var(--forest-dark))]"
          >
            {t(ctaKey)}
          </a>
          <p className="mt-2 text-center text-[10px] text-[hsl(var(--ink-soft))]">
            {t("tryApp.note")}
          </p>
        </div>
      </div>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
